import React, { useMemo, useRef, useState } from "react";
import { Dice5 } from "lucide-react";
import { parseISO, format } from "date-fns";
import { fr } from "date-fns/locale";
import { useLaboBall } from "./LaboBall";
import {
  generateRandomGrid,
  generateWeightedGrid,
  multiScenarioVote,
  countMatches,
  KENO_MAX_NUMBER,
  type LaboDraw,
} from "./laboStats";
import styles from "./labo.module.css";

interface Props {
  tiragesDesc: LaboDraw[];
  /** Tirages d'un exemple figé (famille « outils » ouverte), pas les derniers tirages. */
  exemple?: boolean;
  /** Grille obtenue (null tant qu'aucune) : l'encart d'inscription propose alors de la garder. */
  onGrid?: (grid: number[] | null) => void;
}

export type Mode = "aleatoire" | "pondere" | "vote";

const MODES: { key: Mode; label: string }[] = [
  { key: "aleatoire", label: "Au hasard" },
  { key: "pondere", label: "Numéros les plus fréquents" },
  { key: "vote", label: "Fréquents renforcés" },
];

// De 4 à 10 numéros, comme le règlement du Keno FDJ.
const SIZES = [4, 5, 6, 7, 8, 9, 10];

// Rappel de prudence sous le mode qui s'appuie sur les fréquences, au plus près de l'action (ANJ).
const INDEPENDANCE = "Ces numéros n'ont pas plus de chances de sortir au prochain tirage : chaque tirage est indépendant.";

/** Aide sous le mode choisi. Pas de « plus de chances » à côté d'un tirage : le générateur « pioche plus souvent » (ANJ). */
export function modeNote(mode: Mode, exemple: boolean, nbTirages: number): string {
  if (mode === "aleatoire") return "Une grille tirée totalement au hasard, comme le Flash de la FDJ.";
  if (mode === "pondere") {
    const source = exemple ? "dans l'exemple" : `dans les ${nbTirages} derniers tirages`;
    return `Le générateur pioche plus souvent les numéros sortis le plus souvent ${source}. ${INDEPENDANCE}`;
  }
  return `On tire 12 grilles parmi les numéros fréquents et on garde les numéros qui reviennent le plus souvent. ${INDEPENDANCE}`;
}

/** Grille dite en toutes lettres pour les lecteurs d'écran : « 2, 12, 14 et 54 » (pas « 2121454 »). */
export function grilleEnMots(grid: number[]): string {
  if (grid.length <= 1) return grid.join("");
  return `${grid.slice(0, -1).join(", ")} et ${grid[grid.length - 1]}`;
}

/** Une colonne sous 900 px : la carte résultat est sous les réglages, la page doit y descendre. */
function uneColonne(): boolean {
  try {
    return window.matchMedia("(max-width: 900px)").matches;
  } catch {
    return false;
  }
}

function mouvementReduit(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Outil « Générateur de grille » (← classeurs « MATRICE » / « KENO + »).
 * Trois modes : aléatoire pur, pondéré par les fréquences observées, ou vote
 * multi-scénarios. Affiche la coïncidence avec le dernier tirage.
 */
export const GenerateurGrilleTool: React.FC<Props> = ({ tiragesDesc, exemple = false, onGrid }) => {
  const Ball = useLaboBall();
  const [size, setSize] = useState(7);
  const [mode, setMode] = useState<Mode>("aleatoire");
  const [grid, setGrid] = useState<number[] | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Poids = nombre de sorties observées par numéro (1-based).
  const weights = useMemo(() => {
    const w = new Array(KENO_MAX_NUMBER + 1).fill(0);
    for (const t of tiragesDesc) {
      for (const n of t.numeros) if (n >= 1 && n <= KENO_MAX_NUMBER) w[n]++;
    }
    return w;
  }, [tiragesDesc]);

  const lastDraw = tiragesDesc[0] ?? null;
  const matches = useMemo(
    () => (grid && lastDraw ? countMatches(grid, lastDraw.numeros) : null),
    [grid, lastDraw]
  );

  const generate = () => {
    let next: number[];
    if (mode === "aleatoire") {
      next = generateRandomGrid(size);
    } else if (mode === "pondere") {
      next = generateWeightedGrid(size, weights);
    } else {
      const scenarioSize = Math.max(size, 12);
      next = multiScenarioVote(size, 12, scenarioSize, () =>
        generateWeightedGrid(scenarioSize, weights)
      ).grid;
    }
    setGrid(next);
    onGrid?.(next);
    // Une colonne : la grille s'affiche sous le pli (et sous la barre basse) ; la page la montre.
    if (uneColonne()) {
      window.requestAnimationFrame(() =>
        resultRef.current?.scrollIntoView?.({ block: "nearest", behavior: mouvementReduit() ? "auto" : "smooth" })
      );
    }
  };

  return (
    <div className={styles.genLayout}>
      <div className={styles.card}>
        <div className={styles.genControlBlock}>
          <div className={styles.genField}>
            <span className={styles.controlLabel}>Taille de la grille</span>
            <div className={`${styles.segmented} ${styles.segmentedTailles}`} role="group" aria-label="Taille">
              {SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.segmentedBtn} ${size === s ? styles.segmentedBtnActive : ""}`}
                  aria-pressed={size === s}
                  onClick={() => setSize(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.genField}>
            <span className={styles.controlLabel}>Mode</span>
            <div className={`${styles.segmented} ${styles.segmentedListe}`} role="group" aria-label="Mode">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`${styles.segmentedBtn} ${mode === m.key ? styles.segmentedBtnActive : ""}`}
                  aria-pressed={mode === m.key}
                  onClick={() => setMode(m.key)}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className={styles.sectionHint}>{modeNote(mode, exemple, tiragesDesc.length)}</p>
          </div>

          <button type="button" className={styles.genBtn} onClick={generate}>
            <Dice5 size={18} /> Générer une grille
          </button>
        </div>
      </div>

      <div className={`${styles.card} ${styles.genResultCard}`} ref={resultRef}>
        <h2 className={styles.resultTitle}>Votre grille</h2>
        {/* Annonce en mots : les boules seules se liraient comme un seul nombre. */}
        <p className={styles.srOnly} aria-live="polite">
          {grid ? `Votre grille : ${grilleEnMots(grid)}.` : ""}
        </p>
        <div className={styles.genGridDisplay}>
          {grid ? (
            grid.map((n) => <Ball key={n} number={n} />)
          ) : (
            <p className={styles.genPlaceholder}>
              Choisissez une taille et un mode, puis générez votre grille.
            </p>
          )}
        </div>

        {grid && lastDraw && (
          <div className={styles.historyBlock}>
            <div className={styles.genMatchLabel}>
              {exemple ? "Coïncidence avec le dernier tirage de l'exemple" : "Coïncidence avec le dernier tirage"}
              {lastDraw.dateTirage
                ? ` (${format(parseISO(String(lastDraw.dateTirage).slice(0, 10)), "d MMM yyyy", { locale: fr })})`
                : ""}
            </div>
            <p className={styles.verdictSentence} style={{ fontSize: "1.05rem" }}>
              <span className={styles.verdictAccent}>{matches}</span> numéro
              {matches !== null && matches > 1 ? "s" : ""} en commun sur {grid.length}.
            </p>
          </div>
        )}

      </div>
    </div>
  );
};
